import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useMedicines } from '../../hooks/useMedicines';
import ReactECharts from 'echarts-for-react';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiDownload, FiCalendar, FiTrendingUp, FiPieChart, FiBarChart3 } = FiIcons;

const Reports = () => {
  const { medicines } = useMedicines();
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedReport, setSelectedReport] = useState('adherence');

  // Mock data generation for reports
  const generateAdherenceData = () => {
    const days = Array.from({ length: 30 }, (_, i) => {
      const date = subDays(new Date(), 29 - i);
      return {
        date: format(date, 'MMM d'),
        adherence: Math.floor(Math.random() * 30) + 70, // 70-100%
        scheduled: Math.floor(Math.random() * 5) + 3, // 3-8 medicines
        taken: Math.floor(Math.random() * 3) + 5 // 5-8 taken
      };
    });
    return days;
  };

  const generateMedicineBreakdown = () => {
    return medicines.map(medicine => ({
      name: medicine.name,
      adherence: Math.floor(Math.random() * 30) + 70,
      totalDoses: Math.floor(Math.random() * 50) + 30,
      missedDoses: Math.floor(Math.random() * 10) + 2
    }));
  };

  const adherenceData = generateAdherenceData();
  const medicineBreakdown = generateMedicineBreakdown();

  // Chart configurations
  const adherenceTrendChart = {
    title: {
      text: 'Adherence Trend (Last 30 Days)',
      textStyle: { fontSize: 16, fontWeight: 'bold', color: '#1f2937' }
    },
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        const data = params[0];
        return `${data.name}<br/>Adherence: ${data.value}%`;
      }
    },
    xAxis: {
      type: 'category',
      data: adherenceData.map(d => d.date),
      axisLabel: { interval: 4, rotate: 45 }
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 100,
      axisLabel: { formatter: '{value}%' }
    },
    series: [{
      data: adherenceData.map(d => d.adherence),
      type: 'line',
      smooth: true,
      lineStyle: { color: '#3b82f6', width: 3 },
      itemStyle: { color: '#3b82f6' },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(59, 130, 246, 0.3)' },
            { offset: 1, color: 'rgba(59, 130, 246, 0.05)' }
          ]
        }
      }
    }]
  };

  const medicineBreakdownChart = {
    title: {
      text: 'Medicine Adherence Breakdown',
      textStyle: { fontSize: 16, fontWeight: 'bold', color: '#1f2937' }
    },
    tooltip: {
      trigger: 'item',
      formatter: '{b}: {c}% adherence'
    },
    series: [{
      type: 'pie',
      radius: ['40%', '70%'],
      data: medicineBreakdown.map(med => ({
        value: med.adherence,
        name: med.name,
        itemStyle: {
          color: `hsl(${Math.random() * 360}, 70%, 60%)`
        }
      })),
      label: {
        formatter: '{b}\n{c}%'
      }
    }]
  };

  const weeklyPatternChart = {
    title: {
      text: 'Weekly Adherence Pattern',
      textStyle: { fontSize: 16, fontWeight: 'bold', color: '#1f2937' }
    },
    tooltip: { trigger: 'axis' },
    xAxis: {
      type: 'category',
      data: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    },
    yAxis: { type: 'value', axisLabel: { formatter: '{value}%' } },
    series: [{
      data: [92, 89, 94, 87, 91, 85, 88],
      type: 'bar',
      itemStyle: {
        color: {
          type: 'linear',
          x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: '#10b981' },
            { offset: 1, color: '#34d399' }
          ]
        }
      },
      barWidth: '60%'
    }]
  };

  const reportTypes = [
    { id: 'adherence', label: 'Adherence Trend', icon: FiTrendingUp },
    { id: 'breakdown', label: 'Medicine Breakdown', icon: FiPieChart },
    { id: 'pattern', label: 'Weekly Pattern', icon: FiBarChart3 }
  ];

  const periods = [
    { id: 'week', label: 'Last 7 Days' },
    { id: 'month', label: 'Last 30 Days' },
    { id: 'quarter', label: 'Last 3 Months' },
    { id: 'year', label: 'Last Year' }
  ];

  const getCurrentChart = () => {
    switch (selectedReport) {
      case 'adherence': return adherenceTrendChart;
      case 'breakdown': return medicineBreakdownChart;
      case 'pattern': return weeklyPatternChart;
      default: return adherenceTrendChart;
    }
  };

  const exportReport = () => {
    // In a real app, this would generate and download a PDF report
    alert('Report export functionality would be implemented here');
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reports & Analytics</h1>
          <p className="text-gray-600 mt-2">Detailed insights into your medicine adherence</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={exportReport}
          className="flex items-center space-x-2 bg-gradient-to-r from-purple-500 to-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-purple-600 hover:to-blue-700 transition-all duration-200"
        >
          <SafeIcon icon={FiDownload} />
          <span>Export Report</span>
        </motion.button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Overall Adherence</p>
              <p className="text-3xl font-bold">91%</p>
              <p className="text-green-100 text-xs">+3% from last month</p>
            </div>
            <SafeIcon icon={FiTrendingUp} className="text-2xl text-green-100" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-6 border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Total Doses Taken</p>
              <p className="text-3xl font-bold text-gray-900">247</p>
              <p className="text-green-600 text-xs">+12 from last week</p>
            </div>
            <SafeIcon icon={FiIcons.FiCheckCircle} className="text-2xl text-green-400" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-6 border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Missed Doses</p>
              <p className="text-3xl font-bold text-red-600">23</p>
              <p className="text-red-600 text-xs">-5 from last week</p>
            </div>
            <SafeIcon icon={FiIcons.FiXCircle} className="text-2xl text-red-400" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-6 border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Streak Days</p>
              <p className="text-3xl font-bold text-purple-600">12</p>
              <p className="text-purple-600 text-xs">Personal best!</p>
            </div>
            <SafeIcon icon={FiIcons.FiAward} className="text-2xl text-purple-400" />
          </div>
        </motion.div>
      </div>

      {/* Report Controls */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiCalendar} className="text-gray-600" />
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {periods.map(period => (
                <option key={period.id} value={period.id}>{period.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {reportTypes.map(type => (
            <button
              key={type.id}
              onClick={() => setSelectedReport(type.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                selectedReport === type.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <SafeIcon icon={type.icon} className="text-sm" />
              <span>{type.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Main Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8"
      >
        <div className="h-96">
          <ReactECharts
            option={getCurrentChart()}
            style={{ height: '100%', width: '100%' }}
            opts={{ renderer: 'svg' }}
          />
        </div>
      </motion.div>

      {/* Detailed Medicine Statistics */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Medicine Performance</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-gray-600 font-medium">Medicine</th>
                <th className="text-left py-3 px-4 text-gray-600 font-medium">Adherence Rate</th>
                <th className="text-left py-3 px-4 text-gray-600 font-medium">Total Doses</th>
                <th className="text-left py-3 px-4 text-gray-600 font-medium">Missed</th>
                <th className="text-left py-3 px-4 text-gray-600 font-medium">Streak</th>
              </tr>
            </thead>
            <tbody>
              {medicineBreakdown.map((medicine, index) => (
                <motion.tr
                  key={medicine.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="border-b border-gray-100 hover:bg-gray-50"
                >
                  <td className="py-4 px-4">
                    <div className="font-medium text-gray-900">{medicine.name}</div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-green-400 to-green-500"
                          style={{ width: `${medicine.adherence}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-900">{medicine.adherence}%</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-gray-900">{medicine.totalDoses}</td>
                  <td className="py-4 px-4 text-red-600">{medicine.missedDoses}</td>
                  <td className="py-4 px-4 text-purple-600">{Math.floor(Math.random() * 20) + 5} days</td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default Reports;